import { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { Radio, Crown, ArrowRight, Award } from "lucide-react";
import { Button } from "@/components/ui/button";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Inline LiveCast preview rendered on the public BusinessProfilePage.
 * Shows the most recent published LiveCast for the listing.
 * Tapping any CTA routes to /livecast/{listingSlug}/{castSlug} for the full
 * registration + watch experience.
 */
export default function LivecastSection({ listingSlug, business }) {
  const [cast, setCast] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!listingSlug) return;
    (async () => {
      try {
        const r = await axios.get(`${API}/v1/livecasts/public/by-listing/${listingSlug}`);
        const items = r.data?.livecasts || [];
        setCast(items[0] || null);
      } catch {
        setCast(null);
      } finally {
        setLoading(false);
      }
    })();
  }, [listingSlug]);

  if (loading || !cast) return null;

  const offer = cast.offer || {};
  const v = cast.video || {};
  const link = `/livecast/${listingSlug}/${cast.slug}`;

  return (
    <section
      id="livecast"
      className="rounded-2xl border border-[#F4B41A]/40 bg-gradient-to-br from-[#F4B41A]/[0.08] via-[#0F172A] to-[#1E40AF]/[0.08] overflow-hidden shadow-[0_30px_80px_-30px_rgba(244,180,26,0.30)] scroll-mt-24"
      data-testid="livecast-section"
    >
      <div className="grid lg:grid-cols-2 gap-0">
        {/* Video preview */}
        <Link to={link} className="block relative aspect-video bg-black group">
          {v.thumbnail ? (
            <img src={v.thumbnail} alt={cast.title} className="w-full h-full object-cover" />
          ) : v.embed_url ? (
            <iframe src={v.embed_url} title={cast.title} className="w-full h-full pointer-events-none" style={{ border: 0 }} />
          ) : (
            <div className="w-full h-full bg-gradient-to-br from-[#0F172A] to-[#1E293B] flex items-center justify-center">
              <Radio className="w-12 h-12 text-[#F4B41A]/60" />
            </div>
          )}
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-black/30" />
          <div className="absolute top-3 left-3 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-black/70 backdrop-blur text-[10px] uppercase tracking-widest font-bold">
            <span className="w-1.5 h-1.5 bg-[#F4B41A] rounded-full animate-pulse" /> Watch Now
          </div>
          <div className="absolute top-3 right-3 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-[#F4B41A]/90 text-[#0A0F1C] text-[10px] uppercase tracking-widest font-bold">
            <Crown className="w-3 h-3" /> Best of Best
          </div>
          <div className="absolute inset-0 flex items-center justify-center group-hover:bg-black/20 transition-colors">
            <div className="w-16 h-16 rounded-full bg-[#F4B41A] flex items-center justify-center shadow-[0_0_40px_rgba(244,180,26,0.5)] group-hover:scale-110 transition-transform">
              <svg viewBox="0 0 24 24" fill="currentColor" className="w-7 h-7 text-[#0A0F1C] ml-1"><path d="M8 5v14l11-7z" /></svg>
            </div>
          </div>
        </Link>

        {/* Copy + CTAs */}
        <div className="p-6 lg:p-8 flex flex-col justify-center">
          <p className="text-xs uppercase tracking-[0.25em] text-[#F4B41A] font-bold mb-2 flex items-center gap-2">
            <Radio className="w-3.5 h-3.5" /> Best of Best LiveCast™
          </p>
          <h2 className="text-2xl sm:text-3xl font-bold text-white leading-tight mb-2">{cast.title}</h2>
          {cast.short_description && (
            <p className="text-[#C7D2E0] leading-relaxed mb-4">{cast.short_description}</p>
          )}

          {(cast.host_name || cast.host_title) && (
            <div className="flex items-center gap-2.5 mb-5">
              {cast.host_avatar_url && <img src={cast.host_avatar_url} alt={cast.host_name} className="w-8 h-8 rounded-full object-cover" />}
              <span className="text-sm text-[#94A3B8]">
                <span className="text-white font-medium">{cast.host_name}</span>{cast.host_title ? ` · ${cast.host_title}` : ""}
              </span>
            </div>
          )}

          {(offer.headline || offer.button_text) && (
            <div className="rounded-xl border border-[#F4B41A]/30 bg-[#F4B41A]/[0.05] p-3 mb-5 flex items-center gap-3">
              <Award className="w-5 h-5 text-[#F4B41A] flex-shrink-0" />
              <p className="text-sm text-white"><strong>Exclusive offer inside:</strong> {offer.headline}</p>
            </div>
          )}

          <div className="flex flex-wrap gap-3">
            <Link to={link} className="flex-1 min-w-[150px]">
              <Button className="w-full bg-gradient-to-r from-[#F4B41A] to-[#E0A015] hover:opacity-90 text-[#0A0F1C] font-bold rounded-full py-6 shadow-[0_0_30px_rgba(244,180,26,0.35)]" data-testid="livecast-watch-now-cta">
                Watch Now <ArrowRight className="w-4 h-4 ml-1.5" />
              </Button>
            </Link>
            <Link to={link} className="flex-1 min-w-[150px]">
              <Button variant="outline" className="w-full border-white/20 bg-white/5 hover:bg-white/10 text-white rounded-full py-6" data-testid="livecast-share-cta">
                Share This Event
              </Button>
            </Link>
          </div>
          <p className="text-[10px] text-[#64748B] mt-4">
            Powered by LocalBest.ai · AI Horse-Powered Evergreen Sales Event
          </p>
        </div>
      </div>
    </section>
  );
}
